import React, { useEffect, useRef, useState } from "react";
import * as xlsx from "xlsx-js-style";
// import * as xlsx from "xlsx";
import { Modal, Tab, Tabs } from "react-bootstrap";

export default function ByNames({ possibleSizes }) {
  /*   All States
   ********************************************* */
  // Constants

  // Refs
  const fileInputRef = useRef(null);

  // Excel handling states
  const [loading, setLoading] = useState(false);
  const [wb, setWb] = useState(null);
  // const [ws, setWs] = useState(null);

  const [ws, setWs] = useState([]);
  const [showWorkSheetModal, setShowWorkSheetModal] = useState(false);
  const [columnLabels, setColumnLabels] = useState([]);

  const [jsonData, setJsonData] = useState(null);

  const [teamNameColumn, setTeamNameColumn] = useState("");
  const [ageGroupColumn, setAgeGroupColumn] = useState("");
  const [colorColumn, setColorColumn] = useState("");

  const [playerNameColumn, setPlayerNameColumn] = useState("");
  const [playerNumberColumn, setPlayerNumberColumn] = useState("");

  const [productColumns, setProductColumns] = useState([]);
  const [productSettings, setProductSettings] = useState({});

  // Sorting states
  const [sortColumns, setSortColumns] = useState([]);
  const [sortDirections, setSortDirections] = useState({});

  // Result states
  const [processedData, setProcessedData] = useState([]);
  const [errors, setErrors] = useState([]);

  // Size validation states
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [invalidSizes, setInvalidSizes] = useState({});
  const [replacementSizes, setReplacementSizes] = useState({});
  const [dataValidated, setDataValidated] = useState(false);

  const [selectedTab, setSelectedTab] = useState(null);

  /*   All Functions
   ********************************************* */
  const loadingOn = () => setLoading(true);
  const loadingOff = () => setLoading(false);

  // Excel import function
  const readExcel = (e) => {
    let file = e.target.files[0];
    loadingOn();
    const promise = new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      if (!file) {
        loadingOff();
        return;
      }

      fileReader.readAsArrayBuffer(file);

      fileReader.onload = (e) => {
        const bufferArray = e.target.result;
        const wb = xlsx.read(bufferArray, { type: "buffer" });
        resolve(wb);
        loadingOff();
      };

      fileReader.onerror = (error) => {
        reject(error);
        loadingOff();
      };
    });

    promise.then((d) => {
      setWb(d);
    });
  };

  // Process data from selected sheet
  function getDataFromSelectedSheet(sheetName) {
    resetEverything();
    let selectedSheet = wb.Sheets[sheetName];
    const sheetData = xlsx.utils.sheet_to_json(selectedSheet, { header: 1 });

    const [firstRow, ...restRows] = sheetData;
    setColumnLabels(firstRow || []);

    // Convert to JSON format for processing
    const jsonDataFromSheet = restRows.map((row) => {
      const obj = {};
      firstRow.forEach((header, idx) => {
        obj[header] = row[idx];
      });
      return obj;
    });

    setJsonData(jsonDataFromSheet);
    setShowWorkSheetModal(false);
  }

  // Reset states
  const resetEverything = () => {
    setColumnLabels([]);
    setJsonData([]);
    setSortColumns([]);
    setSortDirections({});
    setTeamNameColumn("");
    setAgeGroupColumn("");
    setPlayerNameColumn("");
    setPlayerNumberColumn("");
    setProcessedData([]);
    setErrors([]);
    setInvalidSizes({});
    setReplacementSizes({});
    setDataValidated(false);
  };

  // Clear all data and reset file input
  const handleClearButton = () => {
    resetEverything();
    setWb(null);
    setWs(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Toggle sort column selection
  const toggleSortColumn = (column) => {
    // set first sortColumn to be teamNameColumn
    if (sortColumns.includes(column)) {
      setSortColumns(sortColumns.filter((col) => col !== column));
      // Toggle direction if already in sort columns
      const newDirections = { ...sortDirections };
      if (newDirections[column]) {
        delete newDirections[column];
      }
      setSortDirections(newDirections);
    } else {
      setSortColumns([...sortColumns, column]);
      // Set default direction to ascending
      setSortDirections({ ...sortDirections, [column]: "asc" });
    }
  };

  // Toggle Product Column - Add default Yes to hasNames, hasNumbers, sort
  const toggleProductColumn = (column) => {
    let _productSettings = {
      hasNames: true,
      hasNumbers: true,
      sort: true,
    };
    if (productColumns.includes(column)) {
      setProductColumns(productColumns.filter((col) => col !== column));
      setProductSettings((prev) => {
        delete prev[column];
        return prev;
      });
    } else {
      setProductColumns([...productColumns, column]);
      setProductSettings({ ...productSettings, [column]: _productSettings });
    }
  };

  // Toggle sort direction
  const toggleSortDirection = (column) => {
    if (sortDirections[column] === "asc") {
      setSortDirections({ ...sortDirections, [column]: "desc" });
    } else {
      setSortDirections({ ...sortDirections, [column]: "asc" });
    }
  };

  // Check for invalid jersey sizes
  const validateData = () => {
    setErrors([]);
    const newErrors = [];

    // Trip leading spaces in jsonData for productColumns
    jsonData.forEach((row) => {
      productColumns.forEach((column) => {
        if (row[column]) {
          row[column] = row[column]?.trim();
        }
      });
    });

    // update jsonData state with trimmed data
    setJsonData((prev) => jsonData);

    // Validate required column selections
    if (!teamNameColumn) {
      newErrors.push("Please select a column for Team Name");
    }

    // Show error if productSettings has show names or number but no column selected
    productColumns.forEach((column) => {
      const settings = productSettings[column];
      if (settings) {
        if (settings.hasNames && !playerNameColumn) {
          newErrors.push(
            `Please select a column for Player Name for ${column}`,
          );
        }
        if (settings.hasNumbers && !playerNumberColumn) {
          newErrors.push(
            `Please select a column for Player Number for ${column}`,
          );
        }
      }
    });

    if (newErrors.length > 0) {
      setErrors(newErrors);
      setProcessedData([]);
      return false;
    }

    const foundInvalidSizes = {};

    // set values for all productColumns in jsonData to be upper case
    jsonData.forEach((row) => {
      productColumns.forEach((column) => {
        if (row[column]) {
          row[column] = row[column]?.toUpperCase();
        }
      });
    });

    // Get list of valid sizes from the global possibleSizes
    const validSizes = possibleSizes.map((item) => item.sizeName);

    productColumns?.forEach((column) => {
      jsonData.forEach((row) => {
        let size = row[column];

        if (foundInvalidSizes[column] === undefined) {
          foundInvalidSizes[column] = {};
        }

        size = size?.trim()?.toUpperCase();

        // Check if the size is not in the global possible sizes list
        // and if it is not already marked as invalid
        if (
          size &&
          !validSizes.includes(size) &&
          !foundInvalidSizes[column][size]
        ) {
          foundInvalidSizes[column][size] = true;
        }
      });
    });

    // Check if there are any invalid sizes found
    if (
      Object.keys(foundInvalidSizes).length > 0 &&
      Object.keys(foundInvalidSizes).some((sizeColumn) => {
        return Object.keys(foundInvalidSizes[sizeColumn]).length > 0;
      })
    ) {
      let initialReplacements = {};
      Object.keys(foundInvalidSizes).forEach((sizeColumn) => {
        initialReplacements[sizeColumn] = {}; // Initialize with an empty object

        Object.keys(foundInvalidSizes[sizeColumn]).forEach((size) => {
          initialReplacements[sizeColumn][size] = ""; // Initialize with empty string
        });
      });

      setInvalidSizes(foundInvalidSizes);
      setReplacementSizes(initialReplacements);
      setShowSizeModal(true);
      return false;
    }

    setDataValidated(true);
    return true;
  };

  // get possible sizes for selected product column
  const getSizeColumnRange = (column, sortedData) => {
    // get sizes from the selected column
    const sizes = sortedData.map((row) => row[column]);

    // remove duplicates and filter out empty values
    const uniqueSizes = [...new Set(sizes)].filter((size) => size !== "");

    // sort the sizes based on the sortId in possibleSizes
    const sortedSizes = uniqueSizes.sort((a, b) => {
      const aIndex = possibleSizes.findIndex((item) => item.sizeName === a);
      const bIndex = possibleSizes.findIndex((item) => item.sizeName === b);
      return aIndex - bIndex;
    });

    // filter out empty values, spaces, undefined, and null from sortedSizes
    const filteredSizes = sortedSizes.filter((size) => {
      if (size === undefined || size === null) return false;
      if (typeof size === "string") {
        const trimmed = size.trim();
        return trimmed !== "";
      }
      return false;
    });

    // get first & last entry in sortedSizes and slice the possibleSizes to get all sizes in that range of possibleSizes
    const firstSize = filteredSizes[0];
    const lastSize = filteredSizes[filteredSizes.length - 1];
    const firstIndex = possibleSizes.findIndex(
      (item) => item.sizeName === firstSize,
    );
    const lastIndex = possibleSizes.findIndex(
      (item) => item.sizeName === lastSize,
    );
    const range = possibleSizes.slice(firstIndex, lastIndex + 1);

    // get array of sizes from the range
    const rangeSizes = range.map((item) => item.sizeName);

    return rangeSizes;
  };

  // Apply size corrections to all rows
  const applySizeCorrections = () => {
    // Create a new array with corrected values
    let correctedData = jsonData.map((row, i) => {
      const newRow = { ...row };
      Object.keys(replacementSizes).forEach((sizeColumn) => {
        const currentSize = row[sizeColumn];

        if (
          currentSize &&
          invalidSizes[sizeColumn][currentSize] &&
          replacementSizes[sizeColumn][currentSize]
        ) {
          newRow[sizeColumn] = replacementSizes[sizeColumn][currentSize];
        }
      });

      return newRow;
    });

    setJsonData(correctedData);
    setShowSizeModal(false);
    setDataValidated(true);
  };

  // Handle change in replacement size
  const handleReplacementChange = (column, invalidSize, newSize) => {
    setReplacementSizes((prev) => {
      const updatedSizes = { ...prev };
      if (!updatedSizes[column]) {
        updatedSizes[column] = {};
      }
      updatedSizes[column][invalidSize] = newSize;
      return updatedSizes;
    });
  };

  const checkIfAllReplacementSizesAreEmpty = () => {
    let allEmpty = true;
    Object.keys(replacementSizes).forEach((sizeColumn) => {
      if (!replacementSizes[sizeColumn]) return; // Skip if no replacements for this column

      let currColEmpty = true;

      Object.keys(replacementSizes[sizeColumn]).forEach((size) => {
        if (replacementSizes[sizeColumn][size] !== "") {
          currColEmpty = false;
        }
      });
      if (currColEmpty) {
        allEmpty = true;
      } else {
        allEmpty = false;
      }
    });
    return allEmpty;
  };

  const createOrderSheet = (teamGroups, sortedData) => {
    const result = [];

    let HeaderRow = [];
    // Add Age Column if selected
    if (ageGroupColumn) {
      HeaderRow.push("Age Group");
    }
    // Add Team Name Column
    HeaderRow.push("Team Name");

    // Add Color Column if selected
    if (colorColumn) {
      HeaderRow.push("Color");
    }

    // Create Header Row ( productColumn + possible sizes + grand total ) for each product
    // Loop through each product column
    productColumns.forEach((productColumn) => {
      // Get possible sizes for the current product column
      const _possibleSizes = getSizeColumnRange(productColumn, sortedData);
      HeaderRow.push(productColumn, ..._possibleSizes, "Grand Total");
    });

    result.push(HeaderRow);

    // Loop through each team group
    Object.entries(teamGroups).forEach(([groupKey, teamRows], i) => {
      // Extract team name & age group from the group key
      const teamName = groupKey.split(" - ")[0] || "Unknown Team";
      const ageGroup = groupKey.split(" - ")[1] || "";

      // Create a new row for each team
      const newRow = [];

      // Add Age Group if selected
      if (ageGroupColumn) {
        newRow.push(ageGroup);
      }
      // Add Team Name
      newRow.push(teamName);

      // Add Color if selected
      if (colorColumn) {
        const color = teamRows[0][colorColumn] || "Unknown Color";
        newRow.push(color);
      }

      // Loop through each product column
      productColumns.forEach((productColumn) => {
        // Get possible sizes for the current product column
        const _possibleSizes = getSizeColumnRange(productColumn, sortedData);

        // Count total Pcs for this team
        // const totalPcs = teamRows.filter((row) => row[productColumn]).length;
        newRow.push("");

        // Count total pcs for each size for this team
        const sizeCounts = teamRows.reduce((acc, row) => {
          const size = row[productColumn];
          if (size) {
            acc[size] = (acc[size] || 0) + 1;
          }
          return acc;
        }, {});

        // Add size counts to the row in order of possible sizes
        _possibleSizes.forEach((size) => {
          newRow.push(sizeCounts[size] || "");
        });

        // Add Grand Total for this product column
        const grandTotal = _possibleSizes.reduce(
          (sum, size) => sum + (sizeCounts[size] || 0),
          0,
        );
        newRow.push(grandTotal);
      });

      result.push(newRow);
    });

    // Add empty row between teams
    let emptyRow = new Array(HeaderRow.length).fill("");
    result.push(emptyRow);

    // Add Grand Total Row which is the last row which sums all the sizes for each product column of every team
    const grandTotalRow = ["Grand Total"];
    // Add empty cell for Age Group and Team Name
    if (ageGroupColumn) {
      grandTotalRow.push("");
    }
    if (colorColumn) {
      grandTotalRow.push("");
    }
    // Loop through each product column
    productColumns.forEach((productColumn) => {
      // Get possible sizes for the current product column
      const _possibleSizes = getSizeColumnRange(productColumn, sortedData);

      // Count total pcs for each size for all teams
      const totalSizeCounts = sortedData.reduce((acc, row) => {
        const size = row[productColumn];
        if (size) {
          acc[size] = (acc[size] || 0) + 1;
        }
        return acc;
      }, {});

      // Add an empty cell for the product name
      grandTotalRow.push("");

      // Add size counts to the grand total row in order of possible sizes
      _possibleSizes.forEach((size) => {
        grandTotalRow.push(totalSizeCounts[size] || "");
      });

      // Add Grand Total for this product column
      const grandTotal = _possibleSizes.reduce(
        (sum, size) => sum + (totalSizeCounts[size] || 0),
        0,
      );
      grandTotalRow.push(grandTotal);
    });
    result.push(grandTotalRow);

    return result;
  };

  // Process data based on column selections
  const processData = () => {
    if (!dataValidated && !validateData()) {
      alert("Please validate the data first!");
      return; // Stop if data isn't validated
    }

    // Sort data
    let sortedData = [...jsonData];
    if (sortColumns.length > 0) {
      sortedData.sort((a, b) => {
        for (const col of sortColumns) {
          const direction = sortDirections[col] === "desc" ? -1 : 1;
          const aVal = a[col] !== undefined ? a[col] : "";
          const bVal = b[col] !== undefined ? b[col] : "";

          const aIsString = typeof aVal === "string";
          const bIsString = typeof bVal === "string";
          const aIsNumber = typeof aVal === "number";
          const bIsNumber = typeof bVal === "number";

          if (aIsString && bIsString) {
            const comparisonResult = aVal.localeCompare(bVal);
            if (comparisonResult !== 0) return comparisonResult * direction;
          } else if (aIsNumber && bIsNumber) {
            if (aVal < bVal) return -1 * direction;
            if (aVal > bVal) return 1 * direction;
          } else {
            // Handle cases where one is a string and the other is a number
            // You might want to define a specific sorting order for these cases.
            // For a simple approach, you could prioritize strings or numbers.
            // Here, we'll treat numbers as coming before strings if types differ.
            if (aIsNumber && bIsString) return -1 * direction;
            if (aIsString && bIsNumber) return 1 * direction;
          }
        }
        return 0;
      });
    }

    // Group by team name AND age group
    const teamGroups = {};
    sortedData.forEach((row) => {
      const teamName = row[teamNameColumn] || "Unknown Team";

      let groupKey = teamName;

      // if ageGroupColumn is selected, add it to the groupKey;
      if (ageGroupColumn) {
        groupKey = `${teamName} - ${row[ageGroupColumn]}`;
      }

      if (!teamGroups[groupKey]) {
        teamGroups[groupKey] = [];
      }
      teamGroups[groupKey].push(row);
    });

    // Process each team group
    const sheetsData = {};

    productColumns.forEach((productColumn) => {
      /* 
      get list of all unique sizes in product column.
      arrange them in order of possible sizes.
      remove sizes that are in possible sizes and not in product column sorted in order of original _possibleSizes.
      */

      // Get possible sizes for the current product column
      const _possibleSizes = getSizeColumnRange(productColumn, sortedData);

      const result = [];

      /*   Looping through each team group
       ********************************************* */
      Object.entries(teamGroups).forEach(([groupKey, teamRows], i) => {
        // Extract team name & age group from the group key
        const teamName = groupKey.split(" - ")[0] || "Unknown Team";
        const ageGroup = groupKey.split(" - ")[1] || "";

        // Filter out rows that don't have the product column

        // sort teamRows by playerNameColumn and playerNumberColumn if productSettings has sort true and has names and numbers
        const hasNames = productSettings[productColumn]?.hasNames;
        const hasNumbers = productSettings[productColumn]?.hasNumbers;
        const sort = productSettings[productColumn]?.sort;

        /*   Sorting Columns
         ********************************************* */
        if (sort && hasNames && hasNumbers) {
          teamRows.sort((a, b) => {
            // Only sort by player numbers
            const hasNumberA =
              a[playerNumberColumn] !== undefined &&
              a[playerNumberColumn] !== null &&
              a[playerNumberColumn] !== "";
            const hasNumberB =
              b[playerNumberColumn] !== undefined &&
              b[playerNumberColumn] !== null &&
              b[playerNumberColumn] !== "";

            // Convert to strings for consistent sorting of alphanumeric values
            const numberA = String(a[playerNumberColumn] || "");
            const numberB = String(b[playerNumberColumn] || "");

            if (hasNumberA && hasNumberB) {
              // If both have numbers, sort by number only
              return numberA.localeCompare(numberB, undefined, {
                numeric: true,
              });
            } else if (hasNumberA) {
              return -1; // A has number, B doesn't, so A comes first
            } else if (hasNumberB) {
              return 1; // B has number, A doesn't, so B comes first
            } else {
              // Neither has numbers, fall back to sorting by name
              const nameA = a[playerNameColumn] || "";
              const nameB = b[playerNameColumn] || "";
              return nameA.localeCompare(nameB);
            }
          });
        } else if (sort && hasNames && !hasNumbers) {
          // Sort by name only
          teamRows.sort((a, b) => {
            const nameA = a[playerNameColumn] || "";
            const nameB = b[playerNameColumn] || "";
            return nameA.localeCompare(nameB);
          });
        } else if (sort && hasNumbers && !hasNames) {
          // Sort by number only
          teamRows.sort((a, b) => {
            // Convert to strings for consistent sorting of alphanumeric values
            const numberA = String(a[playerNumberColumn] || "");
            const numberB = String(b[playerNumberColumn] || "");
            return numberA.localeCompare(numberB);
          });
        }

        // Count total Pcs for this team
        const totalPcs = teamRows.filter((row) => row[productColumn]).length;

        // Count total pcs for each size for this team
        const sizeCounts = teamRows.reduce((acc, row) => {
          const size = row[productColumn];
          if (size) {
            acc[size] = (acc[size] || 0) + 1;
          }
          return acc;
        }, {});

        let colorOfTeam = teamRows[0]?.[colorColumn] || "";

        /*   Processing Data
         ********************************************* */
        // Create header row
        const headerRow = {
          type: "header",
          teamName,
          teamColor: colorOfTeam,
          totalPcs: totalPcs,
          display: ageGroup
            ? `${
                i + 1
              }) ${ageGroup} -  ${teamName} ${productColumn} (${totalPcs} pcs) ${
                "(" + colorOfTeam + ")"
              }`
            : `${i + 1}) ${teamName} ${productColumn} (${totalPcs} pcs) ${
                "(" + colorOfTeam + ")"
              }`,
          sizes: _possibleSizes,
        };

        result.push(headerRow);

        // Create size column headers
        const sizeRow = {
          type: "sizeHeader",
          sizes: _possibleSizes.map((size) => ({
            size,
            nameColumn: hasNames ? `${size}` : `${size}`,
            numberColumn: `${size} #`,
          })),
        };

        result.push(sizeRow);

        // Create player distribution by size
        const sizeDistribution = {};

        // Initialize size distribution object
        _possibleSizes.forEach((size) => {
          sizeDistribution[size] = [];
        });

        // Group players by Product Column
        teamRows.forEach((player) => {
          const Size = player[productColumn];
          const playerName = hasNames ? player[playerNameColumn] || "" : "";
          const playerNumber =
            playerNumberColumn && hasNumbers ? player[playerNumberColumn] : "";

          if (_possibleSizes.includes(Size)) {
            sizeDistribution[Size].push({
              name: playerName,
              number: playerNumber,
            });
          }
        });

        // Create player rows
        const maxPlayersInAnySize = Math.max(
          ...Object.values(sizeDistribution).map((players) => players.length),
        );

        if (hasNames || hasNumbers) {
          for (let i = 0; i < maxPlayersInAnySize; i++) {
            const playerRow = {
              type: "playerRow",
              rowIndex: i,
              players: {},
              sizes: _possibleSizes,
            };

            _possibleSizes.forEach((size) => {
              if (sizeDistribution[size][i]) {
                let playerNumber = sizeDistribution[size][i].number;

                // Fix for zero values - explicitly check for 0 instead of using truthy/falsy
                if (
                  typeof playerNumber === "string" &&
                  playerNumber.startsWith("0")
                ) {
                  playerNumber = `'${playerNumber}`; // Add single quote to preserve leading zero
                }

                // Make sure 0 is handled correctly and not converted to empty string
                playerRow.players[size] = {
                  name: sizeDistribution[size][i].name,
                  number: playerNumber === 0 ? 0 : playerNumber, // This ensures 0 stays as 0
                };
              }
            });

            result.push(playerRow);
          }
        } else {
          // If no names or numbers, create only one row with size counts
          const playerRow = {
            type: "playerRow",
            rowIndex: 0,
            players: {},
            sizes: _possibleSizes,
          };
          _possibleSizes.forEach((size) => {
            const count = sizeCounts[size] || 0;
            // Display "0 pcs" instead of just 0 for empty sizes
            playerRow.players[size] = {
              name: count > 0 ? `${count} Pcs` : "0 pcs",
              number: count > 0 ? `${count} Pcs` : "0 pcs",
            };
          });
          result.push(playerRow);
        }

        // Add a blank row between teams
        result.push({ type: "spacer", sizes: _possibleSizes });

        // console.log(result);

        sheetsData[productColumn] = result;
      });
    });

    // create Order Sheet
    const summarySheet = createOrderSheet(teamGroups, sortedData);

    sheetsData["Order Sheet"] = summarySheet;
    setProcessedData(sheetsData);
  };

  // Without Styling Export data to Excel ( Empty cell as null/undefined )
  const exportToExcel = () => {
    if (processedData.length === 0) return;

    // Create a new workbook
    const wb = xlsx.utils.book_new();

    Object.keys(processedData)?.forEach((productColumn) => {
      // Check if this is the Order Sheet summary or a regular product sheet
      if (productColumn === "Order Sheet") {
        // For Order Sheet, we need to handle the 2D array format
        const summaryData = processedData[productColumn];

        // Convert empty strings to null for truly empty cells
        const cleanedData = summaryData.map((row) =>
          row.map((cell) => (cell === "" ? null : cell)),
        );

        const ws = xlsx.utils.aoa_to_sheet(cleanedData);

        // Apply header styling to first row
        const firstRowRange = xlsx.utils.decode_range(ws["!ref"]);
        for (let col = firstRowRange.s.c; col <= firstRowRange.e.c; col++) {
          const cellAddress = xlsx.utils.encode_cell({ r: 0, c: col });
          if (!ws[cellAddress]) ws[cellAddress] = {};
          if (!ws[cellAddress].s) ws[cellAddress].s = {};
          ws[cellAddress].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "E6F2FF" } },
          };
        }

        // Also apply styling to the last row (totals)
        const lastRow = summaryData.length - 1;
        for (let col = firstRowRange.s.c; col <= firstRowRange.e.c; col++) {
          const cellAddress = xlsx.utils.encode_cell({ r: lastRow, c: col });
          if (!ws[cellAddress]) ws[cellAddress] = {};
          if (!ws[cellAddress].s) ws[cellAddress].s = {};
          ws[cellAddress].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "F2F2F2" } },
          };
        }

        // Clean up product column name for sheet name
        const sheetName = "Order-Summary";
        xlsx.utils.book_append_sheet(wb, ws, sheetName);
      } else {
        // For regular product sheets, handle as before
        const wsData = [];
        let exelDataForCurrentProductColumn = processedData[productColumn];

        exelDataForCurrentProductColumn.forEach((row, index) => {
          if (row.type === "header") {
            wsData.push([row.display]);
          } else if (row.type === "sizeHeader") {
            const headerRow = [];
            row.sizes.forEach((sizeInfo) => {
              headerRow.push(sizeInfo.nameColumn, sizeInfo.numberColumn);
            });
            wsData.push(headerRow);
          } else if (row.type === "playerRow") {
            const playerDataRow = [];
            row?.sizes?.forEach((size) => {
              const player = row.players[size];
              if (player) {
                playerDataRow.push(
                  player.name || null, // Use null instead of "-"
                  player.number !== undefined && player.number !== null
                    ? player.number
                    : null, // Use null instead of "-"
                );
              } else {
                playerDataRow.push(null, null); // Use null instead of empty strings
              }
            });
            wsData.push(playerDataRow);
          } else if (row.type === "spacer") {
            wsData.push([]);
          }
        });

        // Create worksheet
        const ws = xlsx.utils.aoa_to_sheet(wsData);

        // Clean up product column name for sheet name
        const cleanProductName = productColumn.replace(/[^a-zA-Z0-9]/g, "-");
        xlsx.utils.book_append_sheet(wb, ws, cleanProductName);
      }
    });

    // Generate filename with date and time
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(
      today.getMonth() + 1,
    ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const timeStr = `${String(today.getHours()).padStart(2, "0")}${String(
      today.getMinutes(),
    ).padStart(2, "0")}`;
    const fileName = `jersey_orders_by_team_${dateStr}_${timeStr}.xlsx`;

    console.log("exported with styles");

    // Write file
    xlsx.writeFile(wb, fileName);
  };

  const exportToExcelWithStyles = () => {
    if (!processedData || Object.keys(processedData).length === 0) return;

    const wb = xlsx.utils.book_new();

    // Styles defined to match UI exactly
    const styles = {
      sectionHeader: {
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        fill: { fgColor: { rgb: "4472C4" } }, // UI Blue
        alignment: { horizontal: "left", vertical: "center" },
      },
      sizeHeader: {
        font: { bold: true },
        fill: { fgColor: { rgb: "D9D9D9" } }, // UI Grey
        alignment: { horizontal: "center" },
        border: {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        },
      },
      playerCell: {
        alignment: { horizontal: "center" },
        border: {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        },
      },
    };

    Object.keys(processedData)?.forEach((productColumn) => {
      if (productColumn === "Order Sheet") {
        const summaryData = processedData[productColumn];
        const ws = xlsx.utils.aoa_to_sheet(summaryData);

        if (ws["!ref"]) {
          const range = xlsx.utils.decode_range(ws["!ref"]);
          for (let r = range.s.r; r <= range.e.r; r++) {
            for (let c = range.s.c; c <= range.e.c; c++) {
              const addr = xlsx.utils.encode_cell({ r, c });
              if (!ws[addr]) continue;
              if (r === 0) {
                ws[addr].s = {
                  font: { bold: true },
                  fill: { fgColor: { rgb: "E6F2FF" } },
                  border: styles.playerCell.border,
                };
              } else if (r === range.e.r) {
                ws[addr].s = {
                  font: { bold: true },
                  fill: { fgColor: { rgb: "F2F2F2" } },
                  border: styles.playerCell.border,
                };
              } else {
                ws[addr].s = { border: styles.playerCell.border };
              }
            }
          }
        }
        xlsx.utils.book_append_sheet(wb, ws, "Order-Summary");
      } else {
        const wsData = [];
        const merges = [];
        let excelData = processedData[productColumn];

        excelData.forEach((row) => {
          const currIdx = wsData.length;

          if (row.type === "header") {
            const colCount = row.sizes.length * 2;
            const rowContent = [{ v: row.display, s: styles.sectionHeader }];
            // Fill with empty strings, not nulls
            for (let i = 1; i < colCount; i++)
              rowContent.push({ v: "", s: styles.sectionHeader });

            wsData.push(rowContent);
            merges.push({
              s: { r: currIdx, c: 0 },
              e: { r: currIdx, c: Math.max(0, colCount - 1) },
            });
          } else if (row.type === "sizeHeader") {
            const headerRow = [];
            row.sizes.forEach((sizeInfo) => {
              headerRow.push(
                { v: sizeInfo.nameColumn, s: styles.sizeHeader },
                { v: sizeInfo.numberColumn, s: styles.sizeHeader },
              );
            });
            wsData.push(headerRow);
          } else if (row.type === "playerRow") {
            const playerDataRow = [];
            row?.sizes?.forEach((size) => {
              const player = row.players[size];
              // Reverted to your exact logic: empty string instead of null
              playerDataRow.push(
                { v: player?.name || "", s: styles.playerCell },
                {
                  v:
                    player?.number !== undefined && player?.number !== null
                      ? player.number
                      : "",
                  s: styles.playerCell,
                },
              );
            });

            wsData.push(playerDataRow);
          } else if (row.type === "spacer") {
            wsData.push([]);
          }
        });

        const ws = xlsx.utils.aoa_to_sheet(wsData);
        ws["!merges"] = merges;
        ws["!cols"] = Array(30).fill({ wch: 12 });

        const cleanProductName = productColumn
          .replace(/[^a-zA-Z0-9]/g, "-")
          .substring(0, 31);
        xlsx.utils.book_append_sheet(wb, ws, cleanProductName);
      }
    });

    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const timeStr = `${String(today.getHours()).padStart(2, "0")}${String(today.getMinutes()).padStart(2, "0")}`;
    const fileName = `jersey_orders_by_team_${dateStr}_${timeStr}.xlsx`;

    xlsx.writeFile(wb, fileName);
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    if (wb) {
      resetEverything();
      setWs(wb.SheetNames);
      setShowWorkSheetModal(true);
    }
  }, [wb]);

  if (!possibleSizes || possibleSizes.length === 0) {
    return (
      <div className="container-fluid ps-0 overflow-y-auto h-100 d-flex align-items-center justify-content-center">
        <h4 className="mb-0 text-danger">
          No possible sizes found. Please check your settings.
        </h4>
      </div>
    );
  }

  return (
    <div className="container-fluid ps-0 overflow-y-auto h-100">
      <div className="container-fluid m-0 d-flex align-items-center justify-content-between">
        <h2 className="mb-0">Team Order By Names</h2>
      </div>

      {/* Layout Section  */}
      <div
        className="row container-fluid mt-2 me-0 pe-2"
        style={{ height: "calc(100vh - 100px)", overflowY: "auto" }}
      >
        <div className={`${columnLabels.length > 0 && "col-md-7"}`}>
          <div className={`px-0  `}>
            {/* Errors Section */}
            {errors.length > 0 && (
              <div className="alert alert-danger">
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="alert-heading">Errors</h5>
                  <button
                    className="btn-close btn-close-primary"
                    onClick={() => setErrors([])}
                  ></button>
                </div>
                <ul className="mb-0">
                  {errors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Excel Import Section */}
            <div className="card mb-2 shadow-sm">
              <div className="px-3 bg-primary text-white">
                <h5 className="">Import Excel Sheet</h5>
              </div>
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex gap-1 align-items-center">
                    <input
                      onChange={readExcel}
                      ref={fileInputRef}
                      type="file"
                      className="form-control"
                    />

                    {ws && (
                      <button
                        onClick={() => setShowWorkSheetModal(true)}
                        className="btn text-nowrap btn-info"
                      >
                        Change Sheet
                      </button>
                    )}

                    <button
                      onClick={handleClearButton}
                      className="btn btn-danger"
                    >
                      Clear
                    </button>
                  </div>

                  {columnLabels.length > 0 && (
                    <div>
                      <span className="badge fs-5 bg-success">
                        {columnLabels.length} columns and {jsonData.length} rows
                        loaded
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Preview Result */}
            <Tabs
              // defaultActiveKey={productColumns[0]}
              id="uncontrolled-tab-example"
              variant="pills"
              className="mb-1"
              activeKey={selectedTab || Object.keys(processedData)[0]}
              onSelect={(k) => setSelectedTab(k)}
            >
              {Object.keys(processedData).map((productColumn, index) => {
                return (
                  <Tab
                    eventKey={productColumn}
                    title={productColumn}
                    key={index}
                    className="text-nowrap"
                  >
                    {processedData[productColumn]?.length > 0 && (
                      <div className="card rounded-3 overflow-hidden shadow-sm">
                        <div className="px-3 bg-dark text-white d-flex align-items-center justify-content-between">
                          <h5 className="">
                            {productColumn === "Order Sheet"
                              ? "Order Summary"
                              : "Jersey Orders By Team"}
                          </h5>
                          <div className="d-flex gap-2 align-items-center">
                            <button
                              className="btn btn-success btn-sm my-2"
                              onClick={exportToExcel}
                            >
                              Export to excel
                            </button>
                            <button
                              className="btn btn-info btn-sm my-2"
                              onClick={exportToExcelWithStyles}
                            >
                              Export to excel with styles
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          {productColumn === "Order Sheet" ? (
                            /* Order Sheet Summary Table */
                            <div
                              className="table-responsive"
                              style={{ maxHeight: "53vh", overflowY: "auto" }}
                            >
                              <table className="table table-bordered table-striped">
                                <thead className="bg-light">
                                  {processedData[productColumn][0] && (
                                    <tr>
                                      {processedData[productColumn][0].map(
                                        (cell, cellIndex) => (
                                          <th
                                            key={cellIndex}
                                            className="text-center fw-bold bg-info bg-opacity-10"
                                          >
                                            {cell}
                                          </th>
                                        ),
                                      )}
                                    </tr>
                                  )}
                                </thead>
                                <tbody>
                                  {processedData[productColumn]
                                    .slice(1)
                                    .map((row, rowIndex) => (
                                      <tr
                                        key={rowIndex}
                                        className={
                                          rowIndex ===
                                          processedData[productColumn].length -
                                            2
                                            ? "fw-bold bg-light"
                                            : ""
                                        }
                                      >
                                        {row.map((cell, cellIndex) => (
                                          <td
                                            key={cellIndex}
                                            className="text-center"
                                          >
                                            {cell}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            /* Regular Product Sheet Tables */
                            <div
                              className="table-responsive"
                              style={{ maxHeight: "53vh", overflowY: "auto" }}
                            >
                              <table className="table table-bordered">
                                <tbody>
                                  {processedData[productColumn]?.map(
                                    (row, rowIndex) => {
                                      if (row.type === "header") {
                                        return (
                                          <tr
                                            key={`header-${rowIndex}`}
                                            className="bg-light"
                                          >
                                            <td
                                              colSpan={row?.sizes?.length * 2}
                                              className="fw-bold fs-5 bg-info bg-opacity-10"
                                            >
                                              {row.display}
                                            </td>
                                          </tr>
                                        );
                                      } else if (row.type === "sizeHeader") {
                                        return (
                                          <tr
                                            key={`size-header-${rowIndex}`}
                                            className="bg-light text-nowrap"
                                          >
                                            {row.sizes.map(
                                              (sizeInfo, sizeIdx) => (
                                                <React.Fragment
                                                  key={`size-${sizeIdx}`}
                                                >
                                                  <th className="text-center bg-secondary bg-opacity-10">
                                                    {sizeInfo.nameColumn}
                                                  </th>
                                                  <th className="text-center bg-secondary bg-opacity-10">
                                                    {sizeInfo.numberColumn}
                                                  </th>
                                                </React.Fragment>
                                              ),
                                            )}
                                          </tr>
                                        );
                                      } else if (row.type === "playerRow") {
                                        return (
                                          <tr key={`player-${rowIndex}`}>
                                            {row?.sizes?.map(
                                              (size, sizeIdx) => {
                                                const player =
                                                  row.players[size];
                                                return (
                                                  <React.Fragment
                                                    key={`player-size-${sizeIdx}`}
                                                  >
                                                    <td className="text-center">
                                                      {player?.name || ""}
                                                    </td>
                                                    <td className="text-center">
                                                      {player?.number !==
                                                        undefined &&
                                                      player?.number !== null
                                                        ? player.number
                                                        : ""}
                                                    </td>
                                                  </React.Fragment>
                                                );
                                              },
                                            )}
                                          </tr>
                                        );
                                      } else if (row.type === "spacer") {
                                        return (
                                          <tr key={`spacer-${rowIndex}`}>
                                            <td
                                              colSpan={row?.sizes?.length * 2}
                                              style={{ height: "20px" }}
                                            ></td>
                                          </tr>
                                        );
                                      }
                                      return null;
                                    },
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </Tab>
                );
              })}
            </Tabs>
          </div>
        </div>

        {/* Configuration Section */}
        <div className="col-md-5 pe-0 me-0">
          {columnLabels.length > 0 && (
            <div className="">
              {/* Sort Columns Selection */}
              <div className="card mb-2 pb-2">
                <div className="px-3 bg-secondary text-white">
                  <h5 className="">
                    Sort Columns ( Team Name : {teamNameColumn})
                  </h5>
                </div>
                <div className="card-body py-2">
                  <div className="row">
                    <div className="col-md-6">
                      <h4 className="mb-3">Select Group Columns</h4>
                      <div className="mb-2 d-flex align-items-center">
                        <label className="form-label w-50 fw-bold">
                          Team Name Column:
                        </label>
                        <select
                          className="form-select form-select-sm w-50"
                          value={teamNameColumn}
                          onChange={(e) => {
                            setTeamNameColumn(e.target.value);
                          }}
                        >
                          <option value="">Select Column</option>
                          {columnLabels.map((col, idx) => (
                            <option key={idx} value={col}>
                              {col}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mb-2 d-flex align-items-center">
                        <label className="form-label fw-bold w-50">
                          Age Group Column:
                        </label>
                        <select
                          className="form-select form-select-sm w-50"
                          value={ageGroupColumn}
                          onChange={(e) => {
                            setAgeGroupColumn(e.target.value);
                          }}
                        >
                          <option value="">Select Column</option>
                          {columnLabels.map((col, idx) => (
                            <option key={idx} value={col}>
                              {col}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="mb-0 d-flex align-items-center">
                        <label className="form-label fw-bold w-50">
                          Color Column:
                        </label>
                        <select
                          className="form-select form-select-sm w-50"
                          value={colorColumn}
                          onChange={(e) => {
                            setColorColumn(e.target.value);
                          }}
                        >
                          <option value="">Select Column</option>
                          {columnLabels.map((col, idx) => (
                            <option key={idx} value={col}>
                              {col}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      {/* Sort Section */}
                      <h4 className="mb-3">Sort Columns</h4>
                      <div style={{ maxHeight: "100px", overflowY: "auto" }}>
                        {columnLabels.map((column, index) => (
                          <div
                            key={index}
                            className="form-check d-flex align-items-center mb-1"
                          >
                            <input
                              className="form-check-input me-2"
                              type="checkbox"
                              id={`sort-${column}`}
                              checked={sortColumns.includes(column)}
                              onChange={() => toggleSortColumn(column)}
                            />
                            <label
                              className="form-check-label me-2"
                              htmlFor={`sort-${column}`}
                            >
                              {column}
                            </label>

                            {sortColumns.includes(column) && (
                              <button
                                className={`btn btn-sm ${
                                  sortDirections[column] === "asc"
                                    ? "btn-outline-success"
                                    : "btn-outline-danger"
                                }`}
                                onClick={() => toggleSortDirection(column)}
                              >
                                {sortDirections[column] === "asc"
                                  ? "↑ ASC"
                                  : "↓ DESC"}
                              </button>
                            )}

                            {sortColumns.includes(column) && (
                              <span className="badge bg-info ms-2">
                                {sortColumns.indexOf(column) + 1}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Names & Number Column Selection */}
              <div className="card mb-2 shadow-sm">
                <div className="px-3 bg-primary text-white">
                  <h5 className="">Names & Numbers</h5>
                </div>
                <div className="card-body py-3">
                  <div className="mb-2 d-flex align-items-center">
                    <label className="form-label w-50 fw-bold">
                      Player Name Column:
                    </label>
                    <select
                      className="form-select form-select-sm w-50"
                      value={playerNameColumn}
                      onChange={(e) => {
                        setPlayerNameColumn(e.target.value);
                      }}
                    >
                      <option value="">Select Column</option>
                      {columnLabels.map((col, idx) => (
                        <option key={idx} value={col}>
                          {col}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-0 d-flex align-items-center">
                    <label className="form-label fw-bold w-50">
                      Player Number Column:
                    </label>
                    <select
                      className="form-select form-select-sm w-50"
                      value={playerNumberColumn}
                      onChange={(e) => {
                        setPlayerNumberColumn(e.target.value);
                      }}
                    >
                      <option value="">Select Column</option>
                      {columnLabels.map((col, idx) => (
                        <option key={idx} value={col}>
                          {col}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Products Column Selection */}
              <div className="card mb-2 pb-2 ">
                <div className="px-3 bg-warning text-white">
                  <h5 className="">Product Columns</h5>
                </div>

                <div className="card-body pe-1">
                  <div className="row mb-2">
                    <div className="col-md-4 fw-bold">Select Columns</div>
                    <div className="col-md-8 row">
                      <div className="col-4 ps-2 fw-bold px-0">Names</div>
                      <div className="col-4 ps-2 fw-bold px-0">Number</div>
                      <div className="col-4 ps-2 fw-bold px-0">Sort</div>
                    </div>
                  </div>
                  <div
                    style={{
                      maxHeight: "120px",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                  >
                    {columnLabels.map((column, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center row"
                      >
                        <div className="col-md-4 d-flex align-items-start">
                          <input
                            className="form-check-input me-2"
                            type="checkbox"
                            id={`product-${column}`}
                            checked={productColumns.includes(column) || ""}
                            onChange={() => {
                              toggleProductColumn(column);
                            }}
                          />
                          <label
                            className="form-check-label fs-6 me-2"
                            htmlFor={`product-${column}`}
                          >
                            {column}
                          </label>
                        </div>
                        <fieldset
                          disabled={!productColumns.includes(column) || ""}
                          className="col-md-8 row"
                        >
                          <div className="col-4 px-0">
                            <select
                              value={
                                productSettings[column]?.hasNames ? "yes" : "no"
                              }
                              onChange={() => {
                                setProductSettings((prev) => ({
                                  ...prev,
                                  [column]: {
                                    ...prev[column],
                                    hasNames: !prev[column]?.hasNames,
                                  },
                                }));
                              }}
                              className="form-select form-select-sm mb-1"
                            >
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                            </select>
                          </div>
                          <div className="col-4 px-0">
                            <select
                              value={
                                productSettings[column]?.hasNumbers
                                  ? "yes"
                                  : "no"
                              }
                              onChange={() => {
                                setProductSettings((prev) => ({
                                  ...prev,
                                  [column]: {
                                    ...prev[column],
                                    hasNumbers: !prev[column]?.hasNumbers,
                                  },
                                }));
                              }}
                              className=" form-select form-select-sm mb-1"
                            >
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                            </select>
                          </div>
                          <div className="col-4 px-0">
                            <select
                              value={
                                productSettings[column]?.sort ? "yes" : "no"
                              }
                              onChange={() => {
                                setProductSettings((prev) => ({
                                  ...prev,
                                  [column]: {
                                    ...prev[column],
                                    sort: !prev[column]?.sort,
                                  },
                                }));
                              }}
                              className=" form-select form-select-sm mb-1"
                            >
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                            </select>
                          </div>
                        </fieldset>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mx-3 ">
                  <button
                    className={`btn w-100 ${
                      dataValidated ? "btn-success" : "btn-info"
                    }`}
                    onClick={validateData}
                    disabled={!teamNameColumn || !productColumns.length}
                  >
                    {dataValidated
                      ? "Data validated successfully!"
                      : "Validate Data"}
                  </button>
                </div>
              </div>

              {/* Process Data */}
              <div>
                <button
                  className="btn btn-primary btn-lg w-100"
                  // onClick={() => {
                  //   setErrors(["This function is not completed yet."]);
                  // }}
                  onClick={processData}
                  disabled={!teamNameColumn || !productColumns.length}
                >
                  Process Data
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Worksheet Selection Modal */}
      <Modal
        show={showWorkSheetModal}
        onHide={() => setShowWorkSheetModal(false)}
      >
        <Modal.Body className="bg-light">
          <h3>Select WorkSheet</h3>
          <div className="my-3">
            {ws &&
              ws.map((item) => (
                <div
                  key={item}
                  className="bg-white shadow-sm rounded p-2 mb-2 d-flex align-items-center justify-content-between"
                >
                  <div>{item}</div>
                  <div>
                    <button
                      onClick={() => getDataFromSelectedSheet(item)}
                      className="btn btn-sm btn-success"
                    >
                      Select
                    </button>
                  </div>
                </div>
              ))}
          </div>
          <div className="text-end">
            <button
              onClick={() => setShowWorkSheetModal(false)}
              className="btn btn-sm btn-secondary"
            >
              Close
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Invalid Sizes Modal */}
      <Modal
        show={showSizeModal}
        onHide={() => setShowSizeModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header className="bg-warning">
          <Modal.Title>Invalid Jersey Sizes Detected</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            The following jersey sizes were found in your data but are not in
            the standard size list. Please select a replacement size for each:
          </p>

          <div className="mt-3">
            {Object.keys(invalidSizes).map((size, index) => (
              <div key={index} className="mb-3 border-bottom pb-2">
                <h4> {size}</h4>

                {Object.keys(invalidSizes[size]).map((invalidSize, i) => (
                  <div key={i} className="row align-items-center">
                    <div className="col-5">
                      <span className="fw-bold text-danger">{invalidSize}</span>
                    </div>
                    <div className="col-7">
                      <select
                        className="form-select"
                        value={replacementSizes[size][invalidSize] || ""}
                        onChange={(e) =>
                          handleReplacementChange(
                            size,
                            invalidSize,
                            e.target.value,
                          )
                        }
                      >
                        <option value="">Select Replacement Size</option>
                        {possibleSizes.map((sizeOption, i) => (
                          <option key={i} value={sizeOption.sizeName}>
                            {sizeOption.sizeName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="bg-light p-2 rounded mt-3">
            <p className="mb-0">
              <small>
                Note: Applying these changes will update all occurrences of the
                invalid sizes in your data.
              </small>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setShowSizeModal(false)}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={applySizeCorrections}
            disabled={checkIfAllReplacementSizesAreEmpty()}
          >
            Apply Corrections
          </button>
        </Modal.Footer>
      </Modal>

      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75"
          style={{ zIndex: 1050 }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
}
